interface Anchor {
  bottom: number
  cx: number
  cy: number
  left: number
  right: number
  top: number
}

interface Point {
  x: number
  y: number
}

class TimelineRoad {
  car: HTMLElement | null
  dash: null | SVGPathElement
  endY: number
  lastLen: number
  mq: MediaQueryList

  path: null | SVGPathElement
  raf: null | number
  reduce: MediaQueryList
  road: HTMLElement | null
  startY: number
  svg: null | SVGSVGElement
  total: number
  track: HTMLElement
  travelDir: number
  #active = false
  #flip = 1
  #flipDeadzone = 10
  #outerGap = 40
  #radius = 40
  #resizeRaf: null | number = null
  #svgNs = 'http://www.w3.org/2000/svg'

  constructor (track: HTMLElement) {
    this.track = track
    this.road = track.querySelector<HTMLElement>('[data-timeline-road]')
    this.mq = window.matchMedia('(min-width: 1200px)')
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)')

    this.svg = null
    this.path = null
    this.dash = null
    this.car = null
    this.total = 0
    this.startY = 0
    this.endY = 0
    this.lastLen = 0
    this.travelDir = 1
    this.raf = null

    this.onScroll = this.onScroll.bind(this)
    this.onResize = this.onResize.bind(this)
    this.onApply = this.onApply.bind(this)
    this.onStart = this.onStart.bind(this)
  }

  init (): void {
    if (!this.road) return

    this.mq.addEventListener('change', this.onApply)
    // Bei Umschalten der Bewegungspräferenz Auto neu positionieren/anhalten
    this.reduce.addEventListener('change', this.onResize)
    window.addEventListener('resize', this.onResize)
    // Capture-Phase: fängt auch Scrolls eines inneren Containers (online-styleguide)
    window.addEventListener('scroll', this.onScroll, { capture: true, passive: true })

    if ('fonts' in document) {
      void document.fonts.ready.then(this.onStart)
    } else {
      this.onStart()
    }
  }

  onApply (): void {
    this.#apply()
  }

  onResize (): void {
    if (this.#resizeRaf) return
    this.#resizeRaf = window.requestAnimationFrame(() => {
      this.#resizeRaf = null
      if (this.mq.matches) this.#build()
    })
  }

  onScroll (): void {
    if (!this.#active || this.raf || this.reduce.matches) return
    this.raf = window.requestAnimationFrame(() => {
      this.raf = null
      this.#update()
    })
  }

  onStart (): void {
    this.#apply()

    // Straße neu aufbauen, wenn sich die Track-Höhe ändert (z. B. Bilder laden)
    if ('ResizeObserver' in window) {
      new window.ResizeObserver(this.onResize).observe(this.track)
    }
  }

  #anchors (): Anchor[] {
    const items = this.track.querySelectorAll('[data-timeline-item]')
    const tr = this.track.getBoundingClientRect()

    return Array.from(items).map((item) => {
      const card = item.querySelector('.timeline__card') || item
      const rc = card.getBoundingClientRect()
      return {
        bottom: rc.bottom - tr.top,
        cx: rc.left - tr.left + rc.width / 2,
        cy: rc.top - tr.top + rc.height / 2,
        left: rc.left - tr.left,
        right: rc.right - tr.left,
        top: rc.top - tr.top
      }
    })
  }

  #apply (): void {
    if (this.mq.matches) {
      this.#build()
    } else {
      this.#teardown()
    }
  }

  #build (): void {
    const anchors = this.#anchors()
    if (anchors.length < 2 || !this.track.clientHeight) {
      this.#teardown()
      return
    }

    const d = this.#pathData(this.#waypoints(anchors), this.#radius)

    if (!this.svg) {
      this.svg = document.createElementNS(this.#svgNs, 'svg') as SVGSVGElement
      this.svg.setAttribute('preserveAspectRatio', 'none')
      this.svg.setAttribute('fill', 'none')

      this.path = document.createElementNS(this.#svgNs, 'path') as SVGPathElement
      this.path.setAttribute('class', 'timeline__road-line')
      this.svg.appendChild(this.path)

      this.dash = document.createElementNS(this.#svgNs, 'path') as SVGPathElement
      this.dash.setAttribute('class', 'timeline__road-dash')
      this.svg.appendChild(this.dash)

      this.road!.appendChild(this.svg)
    }

    this.svg.setAttribute('viewBox', `0 0 ${this.track.clientWidth} ${this.track.clientHeight}`)
    this.path!.setAttribute('d', d)
    this.dash!.setAttribute('d', d)

    if (!this.car) {
      this.car = document.createElement('div')
      this.car.className = 'timeline__car'
      this.car.setAttribute('aria-hidden', 'true')
      this.track.appendChild(this.car)
    }

    this.total = this.path!.getTotalLength()
    this.startY = anchors[0].cy
    this.endY = anchors[anchors.length - 1].cy

    this.#active = true
    this.#update()
  }

  #pathData (pts: Point[], radius: number): string {
    if (pts.length < 2) return ''
    const r = (n: number): number => Math.round(n * 100) / 100
    let d = `M ${r(pts[0].x)} ${r(pts[0].y)}`

    for (let i = 1; i < pts.length - 1; i++) {
      const p0 = pts[i - 1]
      const p1 = pts[i]
      const p2 = pts[i + 1]

      const v1 = { x: p0.x - p1.x, y: p0.y - p1.y }
      const v2 = { x: p2.x - p1.x, y: p2.y - p1.y }
      const l1 = Math.hypot(v1.x, v1.y)
      const l2 = Math.hypot(v2.x, v2.y)

      if (l1 < 0.5 || l2 < 0.5) {
        d += ` L ${r(p1.x)} ${r(p1.y)}`
        continue
      }

      const rr = Math.min(radius, l1 / 2, l2 / 2)
      const a = { x: p1.x + (v1.x / l1) * rr, y: p1.y + (v1.y / l1) * rr }
      const b = { x: p1.x + (v2.x / l2) * rr, y: p1.y + (v2.y / l2) * rr }

      d += ` L ${r(a.x)} ${r(a.y)} Q ${r(p1.x)} ${r(p1.y)} ${r(b.x)} ${r(b.y)}`
    }

    const last = pts[pts.length - 1]
    d += ` L ${r(last.x)} ${r(last.y)}`
    return d
  }

  #place (len: number): void {
    if (len > this.lastLen + 0.5) this.travelDir = 1
    else if (len < this.lastLen - 0.5) this.travelDir = -1
    this.lastLen = len

    const p = this.path!.getPointAtLength(len)
    const back = this.path!.getPointAtLength(Math.max(len - 2, 0))
    const ahead = this.path!.getPointAtLength(Math.min(len + 2, this.total))
    const vx = (ahead.x - back.x) * this.travelDir
    const vy = (ahead.y - back.y) * this.travelDir

    let ang = Math.atan2(vy, vx) * (180 / Math.PI)
    if (ang > 90 + this.#flipDeadzone || ang < -90 - this.#flipDeadzone) {
      this.#flip = -1
    } else if (ang < 90 - this.#flipDeadzone && ang > -90 + this.#flipDeadzone) {
      this.#flip = 1
    }
    if (this.#flip === -1) ang = ang > 0 ? ang - 180 : ang + 180

    this.car!.style.transform =
      `translate(${p.x}px, ${p.y}px) translate(-50%, -50%) rotate(${ang}deg) scaleX(${this.#flip})`
  }

  #teardown (): void {
    if (!this.#active && !this.car && !this.svg) return
    if (this.raf) window.cancelAnimationFrame(this.raf)
    this.raf = null
    this.road!.innerHTML = ''
    this.svg = null
    this.path = null
    this.dash = null
    if (this.car) {
      this.car.remove()
      this.car = null
    }
    this.#active = false
  }

  #update (): void {
    if (!this.path || !this.car) return

    const offset = Math.min(36, this.total)

    if (this.reduce.matches) {
      this.#place(offset)
      return
    }

    const y = window.innerHeight / 2 - this.track.getBoundingClientRect().top
    const span = Math.max(this.endY - this.startY, 1)
    const progress = Math.min(Math.max((y - this.startY) / span, 0), 1)
    this.#place(offset + progress * (this.total - offset))
  }

  #waypoints (rects: Anchor[]): Point[] {
    const gap = this.#outerGap

    const startDir = rects[1].cx >= rects[0].cx ? 1 : -1
    const pts: Point[] = [{
      x: startDir === 1 ? rects[0].right : rects[0].left,
      y: rects[0].cy
    }]

    for (let i = 0; i < rects.length - 1; i++) {
      const a = rects[i]
      const b = rects[i + 1]
      const dir = b.cx >= a.cx ? 1 : -1
      const corridorX = dir === 1 ? (a.right + b.left) / 2 : (a.left + b.right) / 2

      if (i === 0) {
        const dx = b.cx - a.cx
        const dy = b.cy - a.cy
        const xFar = a.cy < b.top - gap ? b.cx - 0.07 * dx : corridorX
        const xBack = a.cx + 0.2 * dx
        const y1 = Math.max(Math.min(a.cy + 0.25 * dy, b.top - gap), a.cy + 20)
        const y2 = Math.min(Math.max(a.cy + 0.7 * dy, a.bottom + gap, y1 + 20), b.cy - 20)

        pts.push({ x: xFar, y: a.cy })
        pts.push({ x: xFar, y: y1 })
        pts.push({ x: corridorX, y: y1 })
        pts.push({ x: corridorX, y: y2 })
        pts.push({ x: xBack, y: y2 })
        pts.push({ x: xBack, y: b.cy })
        pts.push({ x: b.cx, y: b.cy })
        continue
      }

      const span = Math.max(b.cy - a.bottom, 0)

      pts.push({ x: a.cx, y: a.bottom + 0.4 * span })
      pts.push({ x: corridorX, y: a.bottom + 0.4 * span })
      pts.push({ x: corridorX, y: b.cy })
      pts.push({ x: b.cx, y: b.cy })
    }

    return pts.filter((p, i) => {
      if (i === 0) return true
      const prev = pts[i - 1]
      return Math.hypot(p.x - prev.x, p.y - prev.y) > 0.5
    })
  }
}

export default TimelineRoad
